/**
 * BakeCache — persist deterministic CPU bakes (noise volumes, LUTs, blue-noise
 * tiles) across sessions so a repeat boot restores bytes from IndexedDB
 * instead of regenerating them (Phase 0 of the Wasm-kernel assessment,
 * 2026-09-01: the cloud noise + blue-noise bakes alone were ~0.85 s of boot).
 *
 * Contract — the cache is an accelerator, never a dependency:
 *   · `get()` resolves the cached buffers or null; it NEVER throws and never
 *     waits longer than `timeoutMs` (a stalled/locked IndexedDB must not cost
 *     more than the bake it replaces). A null means "bake it yourself".
 *   · `put()` is fire-and-forget safe: resolves false on any failure
 *     (quota abort, private mode, closed db) and never throws.
 *   · Entries are keyed by a caller string AND a numeric generator version:
 *     bump the version when the algorithm changes and old bytes are ignored.
 *   · Bytes round-trip EXACTLY (ArrayBuffers via structured clone) — the
 *     caller rebuilds its textures with the same parameters it would have
 *     used for a fresh bake, so a cache hit is pixel-identical to a miss.
 */
import { Logger } from '../core/Logger.js';

export interface BakeCacheOptions {
  dbName?: string;
  storeName?: string;
  /** Upper bound on any single read (ms). Default 600 — a 2 MB row under
   *  boot contention can take a few hundred ms; the bakes it replaces cost
   *  more than that, and a stalled store still can't hold boot longer. */
  timeoutMs?: number;
}

interface BakeRow {
  id: string;           // `${key}@${version}`
  key: string;
  version: number;
  buffers: ArrayBuffer[];
  byteLength: number;
  at: number;
}

const DEFAULT_DB = 'enginex-bake-cache';
const DEFAULT_STORE = 'bakes';
const DB_VERSION = 1;

export class BakeCache {
  private static _shared: BakeCache | null = null;
  /** Process-wide instance (one db connection). */
  static shared(): BakeCache {
    return (BakeCache._shared ??= new BakeCache());
  }
  /** Tests / hot reload: drop the shared instance. */
  static resetShared(): void { BakeCache._shared = null; }

  /** True when this runtime exposes IndexedDB at all. */
  static available(): boolean {
    try { return typeof indexedDB !== 'undefined' && indexedDB !== null; } catch { return false; }
  }

  readonly dbName: string;
  readonly storeName: string;
  readonly timeoutMs: number;
  private _db: IDBDatabase | null = null;
  private _open: Promise<IDBDatabase | null> | null = null;
  private _disabled = false;
  /** In-flight / settled reads by row id — a prefetch started at construction
   *  time is what a later get() consumes (boot-time IndexedDB is slow to open). */
  private readonly _inflight = new Map<string, Promise<BakeRow | null>>();

  constructor(opts: BakeCacheOptions = {}) {
    this.dbName = opts.dbName ?? DEFAULT_DB;
    this.storeName = opts.storeName ?? DEFAULT_STORE;
    this.timeoutMs = opts.timeoutMs ?? 600;
  }

  /**
   * Start reading a row now (memoized); `get()` for the same key/version
   * awaits this read instead of starting its own. Call from module
   * constructors so the bytes are in hand by the time a late `init()` would
   * bake — opening IndexedDB during boot can cost hundreds of ms on its own.
   */
  prefetch(key: string, version: number): void {
    if (this._disabled || !BakeCache.available()) return;
    const id = `${key}@${version}`;
    if (this._inflight.has(id)) return;
    const p = this._read(id).catch(() => null);
    this._inflight.set(id, p);
  }

  /** Restore a bake's buffers, or null on miss / version mismatch / any failure / timeout. */
  async get(key: string, version: number): Promise<Uint8Array[] | null> {
    if (this._disabled || !BakeCache.available()) return null;
    try {
      const id = `${key}@${version}`;
      let read = this._inflight.get(id);
      if (!read) { read = this._read(id).catch(() => null); this._inflight.set(id, read); }
      const row = await this._withTimeout(read, null);
      this._inflight.delete(id); // a settled (or abandoned) read is not reused: put() may change the row
      if (!row || row.version !== version || !Array.isArray(row.buffers)) return null;
      return row.buffers.map((b) => new Uint8Array(b));
    } catch (err) {
      Logger.debug('BakeCache', `get(${key}) failed: ${err}`);
      return null;
    }
  }

  /** Store a bake's buffers. Resolves true on success, false otherwise; never throws. */
  async put(key: string, version: number, buffers: ArrayBufferView[]): Promise<boolean> {
    if (this._disabled || !BakeCache.available()) return false;
    try {
      const db = await this._openDb();
      if (!db) return false;
      // Copy exactly the viewed bytes (a view may sit inside a larger buffer).
      this._inflight.delete(`${key}@${version}`);
      const copies = buffers.map((v) => v.buffer.slice(v.byteOffset, v.byteOffset + v.byteLength) as ArrayBuffer);
      const row: BakeRow = {
        id: `${key}@${version}`, key, version, buffers: copies,
        byteLength: copies.reduce((n, b) => n + b.byteLength, 0), at: Date.now(),
      };
      return await new Promise<boolean>((resolve) => {
        try {
          const tx = db.transaction(this.storeName, 'readwrite');
          tx.objectStore(this.storeName).put(row);
          tx.oncomplete = () => resolve(true);
          tx.onerror = () => resolve(false);
          // WHY onabort: a commit-time QuotaExceededError fires ONLY abort
          // (the SaveManager lesson) — without it this promise never settles.
          tx.onabort = () => resolve(false);
        } catch { resolve(false); }
      });
    } catch (err) {
      Logger.debug('BakeCache', `put(${key}) failed: ${err}`);
      return false;
    }
  }

  /** Remove one entry (any version). Never throws. */
  async remove(key: string, version: number): Promise<void> {
    if (this._disabled || !BakeCache.available()) return;
    this._inflight.delete(`${key}@${version}`);
    try {
      const db = await this._openDb();
      if (!db) return;
      await new Promise<void>((resolve) => {
        try {
          const tx = db.transaction(this.storeName, 'readwrite');
          tx.objectStore(this.storeName).delete(`${key}@${version}`);
          tx.oncomplete = () => resolve(); tx.onerror = () => resolve(); tx.onabort = () => resolve();
        } catch { resolve(); }
      });
    } catch { /* best effort */ }
  }

  /** Close the connection (tests / teardown). */
  close(): void {
    try { this._db?.close(); } catch { /* already closed */ }
    this._db = null; this._open = null;
  }

  // ── internals ──
  private _openDb(): Promise<IDBDatabase | null> {
    if (this._db) return Promise.resolve(this._db);
    if (this._open) return this._open;
    this._open = new Promise<IDBDatabase | null>((resolve) => {
      try {
        const req = indexedDB.open(this.dbName, DB_VERSION);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(this.storeName)) db.createObjectStore(this.storeName, { keyPath: 'id' });
        };
        req.onsuccess = () => { this._db = req.result; resolve(this._db); };
        req.onerror = () => { this._disabled = true; resolve(null); };
        (req as IDBOpenDBRequest).onblocked = () => { /* another tab holds an old version — the timeout covers us */ };
      } catch { this._disabled = true; resolve(null); }
    });
    return this._open;
  }

  private async _read(id: string): Promise<BakeRow | null> {
    const db = await this._openDb();
    if (!db) return null;
    return new Promise<BakeRow | null>((resolve) => {
      try {
        const tx = db.transaction(this.storeName, 'readonly');
        const req = tx.objectStore(this.storeName).get(id);
        req.onsuccess = () => resolve((req.result as BakeRow | undefined) ?? null);
        req.onerror = () => resolve(null);
        tx.onabort = () => resolve(null);
      } catch { resolve(null); }
    });
  }

  private _withTimeout<T>(p: Promise<T>, fallback: T): Promise<T> {
    return new Promise<T>((resolve) => {
      const t = setTimeout(() => resolve(fallback), this.timeoutMs);
      p.then((v) => { clearTimeout(t); resolve(v); }, () => { clearTimeout(t); resolve(fallback); });
    });
  }
}
